import React from 'react';
import { ReactComponent as SearchIcon } from 'common-assets/assets/icons/magnifyingGlass.svg';
import { ReactComponent as ArrowDownIcon } from 'common-assets/assets/icons/chevron-down.svg';
import clsx from 'clsx';
import { AutocompleteOptionModel } from '../../tools/Autocomplete.model';
import { IconButton } from '../../../../IconButton/IconButton';

import useStyles from './AutocompleteButton.style';

export interface AutocompleteButtonProps {
  mobileType: 'full' | 'small' | 'fake-input';
  buttonPlaceholder: string;
  option: AutocompleteOptionModel | null;
  getOptionLabel: (option: AutocompleteOptionModel) => string;
  renderIcon?: (option: AutocompleteOptionModel) => React.ReactNode;
  onClick: () => void;
  className?: string;
  isOpen?: boolean;
  ariaDescribedby?: string;
  disabled?: boolean;
  autoTestId?: string;
}

const AutocompleteButton = React.forwardRef<HTMLButtonElement, AutocompleteButtonProps>(({
  mobileType,
  buttonPlaceholder,
  option,
  getOptionLabel,
  renderIcon,
  onClick,
  className,
  isOpen = false,
  ariaDescribedby,
  disabled = false,
  autoTestId,
}, ref) => {
  const c = useStyles({});
  const isOptionSelected = option !== null;
  const label = isOptionSelected ? getOptionLabel(option) : buttonPlaceholder;
  const renderButtonMobileIcon = () => isOptionSelected && renderIcon?.(option);

  const renderButtonContent = () => {
    switch (mobileType) {
      case 'small':
        return (
          <SearchIcon aria-hidden="true" className={c.searchIcon} width={24} height={24}/>
        );
      case 'full':
        return (
          <>
            {renderButtonMobileIcon()}
            <span className={clsx(c.label, { [c.placeholder]: !isOptionSelected })}>
              {label}
            </span>
            <ArrowDownIcon
              aria-hidden="true"
              className={clsx(c.arrowButton, { [c.arrowButtonOpen]: isOpen })}
            />
          </>
        );
      default:
        return (
          <>
            <SearchIcon aria-hidden="true"  className={c.searchIcon} width={24} height={24} />
            <span className={c.fakeInputText}>{buttonPlaceholder}</span>
          </>
        );
    }
  };

  if (mobileType === "small") {
    return (
      <IconButton
        icon={renderButtonContent()}
        ariaLabel={label}
        variant="secondary"
        onClick={onClick}
      />
    );
  }

  return (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      aria-describedby={ariaDescribedby}
      className={clsx({
        [c.button]: true,
        [c.buttonOpen]: isOpen,
        [c.buttonDisabled]: disabled,
        ...(className ? { [className]: true } : {}),
      })}
      onClick={onClick}
      data-autotestid={autoTestId}
    >
      {renderButtonContent()}
    </button>
  );
});

AutocompleteButton.displayName = 'AutocompleteButton';

export {AutocompleteButton}
